import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import axios from 'axios';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  ArrowLeftIcon,
  CheckIcon,
  BuildingStorefrontIcon,
  UserIcon,
  EnvelopeIcon,
  LockClosedIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline';

interface RegisterForm {
  restaurantName: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  plan: 'basic' | 'pro' | 'enterprise';
  acceptTerms: boolean;
}

const plans = {
  basic: { name: 'Basic', price: 49 },
  pro: { name: 'Pro', price: 99 },
  enterprise: { name: 'Enterprise', price: 199 },
};

export default function RegisterPage() {
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  const defaultPlan = (searchParams.get('plan') as keyof typeof plans) || 'pro';

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RegisterForm>({
    defaultValues: {
      plan: defaultPlan,
    },
  });

  const selectedPlan = watch('plan');
  const password = watch('password');

  const onSubmit = async (data: RegisterForm) => {
    setIsLoading(true);

    try {
      // Create tenant and user
      await axios.post('/api/auth/register', {
        restaurantName: data.restaurantName,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone,
        password: data.password,
        plan: data.plan.toUpperCase(),
      });

      toast.success('Cuenta creada correctamente');

      // Generate subdomain from restaurant name
      const slug = data.restaurantName
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');

      // Redirect to the new tenant
      window.location.href = `https://${slug}.restaurantes.sisfarma.info/login`;
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        toast.error(error.response?.data?.message || 'Error al crear la cuenta');
      } else {
        toast.error('Error al crear la cuenta');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-dark-950 flex">
      {/* Left side - Form */}
      <div className="flex-1 flex flex-col justify-center px-4 sm:px-6 lg:px-20 py-12">
        <div className="max-w-md w-full mx-auto">
          {/* Back link */}
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-dark-400 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            Volver al inicio
          </Link>

          {/* Logo */}
          <div className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 bg-primary-600 rounded-xl flex items-center justify-center">
              <span className="text-white font-bold">TPV</span>
            </div>
            <span className="text-xl font-semibold text-white">TPV Restaurante</span>
          </div>

          {/* Steps */}
          <div className="flex items-center gap-4 mb-8">
            {[1, 2, 3].map(s => (
              <div key={s} className="flex items-center gap-2">
                <div
                  className={clsx(
                    'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium',
                    step >= s
                      ? 'bg-primary-600 text-white'
                      : 'bg-dark-800 text-dark-400'
                  )}
                >
                  {step > s ? <CheckIcon className="w-4 h-4" /> : s}
                </div>
                {s < 3 && (
                  <div
                    className={clsx(
                      'w-12 h-0.5',
                      step > s ? 'bg-primary-600' : 'bg-dark-800'
                    )}
                  />
                )}
              </div>
            ))}
          </div>

          <form onSubmit={handleSubmit(onSubmit)}>
            {/* Step 1: Plan selection */}
            {step === 1 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <h1 className="text-2xl font-bold mb-2">Elige tu plan</h1>
                <p className="text-dark-400 mb-6">
                  Puedes cambiar de plan en cualquier momento.
                </p>

                <div className="space-y-3">
                  {Object.entries(plans).map(([key, plan]) => (
                    <label
                      key={key}
                      className={clsx(
                        'block p-4 rounded-xl border-2 cursor-pointer transition-all',
                        selectedPlan === key
                          ? 'border-primary-500 bg-primary-500/10'
                          : 'border-dark-700 hover:border-dark-600'
                      )}
                    >
                      <input
                        type="radio"
                        value={key}
                        {...register('plan')}
                        className="sr-only"
                      />
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-white">{plan.name}</p>
                          <p className="text-sm text-dark-400">
                            {key === 'basic' && 'Hasta 10 mesas'}
                            {key === 'pro' && 'Hasta 50 mesas'}
                            {key === 'enterprise' && 'Mesas ilimitadas'}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-white">{plan.price}€</p>
                          <p className="text-sm text-dark-400">/mes</p>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="btn-primary w-full mt-8"
                >
                  Continuar
                </button>
              </motion.div>
            )}

            {/* Step 2: Restaurant info */}
            {step === 2 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <h1 className="text-2xl font-bold mb-2">Tu restaurante</h1>
                <p className="text-dark-400 mb-6">
                  Cuentanos sobre tu negocio.
                </p>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-dark-300 mb-2">
                      Nombre del restaurante
                    </label>
                    <div className="relative">
                      <BuildingStorefrontIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-dark-500" />
                      <input
                        type="text"
                        {...register('restaurantName', {
                          required: 'El nombre es requerido',
                          minLength: { value: 2, message: 'Minimo 2 caracteres' },
                        })}
                        className="input pl-10"
                        placeholder="Mi Restaurante"
                      />
                    </div>
                    {errors.restaurantName && (
                      <p className="text-danger-500 text-sm mt-1">
                        {errors.restaurantName.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-dark-300 mb-2">
                      Telefono del restaurante
                    </label>
                    <div className="relative">
                      <PhoneIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-dark-500" />
                      <input
                        type="tel"
                        {...register('phone', {
                          required: 'El telefono es requerido',
                        })}
                        className="input pl-10"
                        placeholder="+34 912 345 678"
                      />
                    </div>
                    {errors.phone && (
                      <p className="text-danger-500 text-sm mt-1">
                        {errors.phone.message}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex gap-3 mt-8">
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="btn-secondary flex-1"
                  >
                    Atras
                  </button>
                  <button
                    type="button"
                    onClick={() => setStep(3)}
                    className="btn-primary flex-1"
                  >
                    Continuar
                  </button>
                </div>
              </motion.div>
            )}

            {/* Step 3: Account info */}
            {step === 3 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <h1 className="text-2xl font-bold mb-2">Tu cuenta</h1>
                <p className="text-dark-400 mb-6">
                  Crea tu cuenta de administrador.
                </p>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-dark-300 mb-2">
                        Nombre
                      </label>
                      <div className="relative">
                        <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-dark-500" />
                        <input
                          type="text"
                          {...register('firstName', {
                            required: 'Requerido',
                          })}
                          className="input pl-10"
                          placeholder="Juan"
                        />
                      </div>
                      {errors.firstName && (
                        <p className="text-danger-500 text-sm mt-1">
                          {errors.firstName.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-dark-300 mb-2">
                        Apellidos
                      </label>
                      <input
                        type="text"
                        {...register('lastName', {
                          required: 'Requerido',
                        })}
                        className="input"
                        placeholder="Garcia"
                      />
                      {errors.lastName && (
                        <p className="text-danger-500 text-sm mt-1">
                          {errors.lastName.message}
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-dark-300 mb-2">
                      Email
                    </label>
                    <div className="relative">
                      <EnvelopeIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-dark-500" />
                      <input
                        type="email"
                        {...register('email', {
                          required: 'El email es requerido',
                          pattern: {
                            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                            message: 'Email invalido',
                          },
                        })}
                        className="input pl-10"
                        placeholder="juan@restaurante.com"
                      />
                    </div>
                    {errors.email && (
                      <p className="text-danger-500 text-sm mt-1">
                        {errors.email.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-dark-300 mb-2">
                      Contrasena
                    </label>
                    <div className="relative">
                      <LockClosedIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-dark-500" />
                      <input
                        type="password"
                        {...register('password', {
                          required: 'La contrasena es requerida',
                          minLength: {
                            value: 8,
                            message: 'Minimo 8 caracteres',
                          },
                        })}
                        className="input pl-10"
                        placeholder="••••••••"
                      />
                    </div>
                    {errors.password && (
                      <p className="text-danger-500 text-sm mt-1">
                        {errors.password.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-dark-300 mb-2">
                      Confirmar contrasena
                    </label>
                    <div className="relative">
                      <LockClosedIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-dark-500" />
                      <input
                        type="password"
                        {...register('confirmPassword', {
                          required: 'Confirma la contrasena',
                          validate: value =>
                            value === password || 'Las contrasenas no coinciden',
                        })}
                        className="input pl-10"
                        placeholder="••••••••"
                      />
                    </div>
                    {errors.confirmPassword && (
                      <p className="text-danger-500 text-sm mt-1">
                        {errors.confirmPassword.message}
                      </p>
                    )}
                  </div>

                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      {...register('acceptTerms', {
                        required: 'Debes aceptar los terminos',
                      })}
                      className="w-4 h-4 mt-1 rounded border-dark-600 text-primary-500 focus:ring-primary-500"
                    />
                    <span className="text-sm text-dark-400">
                      Acepto los{' '}
                      <a href="#" className="text-primary-500 hover:underline">
                        terminos y condiciones
                      </a>{' '}
                      y la{' '}
                      <a href="#" className="text-primary-500 hover:underline">
                        politica de privacidad
                      </a>
                    </span>
                  </label>
                  {errors.acceptTerms && (
                    <p className="text-danger-500 text-sm">
                      {errors.acceptTerms.message}
                    </p>
                  )}
                </div>

                <div className="flex gap-3 mt-8">
                  <button
                    type="button"
                    onClick={() => setStep(2)}
                    className="btn-secondary flex-1"
                  >
                    Atras
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="btn-primary flex-1"
                  >
                    {isLoading ? 'Creando...' : 'Crear Cuenta'}
                  </button>
                </div>
              </motion.div>
            )}
          </form>

          <p className="text-center text-dark-400 text-sm mt-8">
            Ya tienes cuenta?{' '}
            <a
              href="https://demo-restaurant.restaurantes.sisfarma.info/login"
              className="text-primary-500 hover:underline"
            >
              Inicia sesion
            </a>
          </p>
        </div>
      </div>

      {/* Right side - Visual */}
      <div className="hidden lg:flex flex-1 bg-dark-900 items-center justify-center p-12">
        <div className="max-w-lg text-center">
          <div className="w-24 h-24 bg-primary-600/20 rounded-3xl flex items-center justify-center mx-auto mb-8">
            <BuildingStorefrontIcon className="w-12 h-12 text-primary-500" />
          </div>
          <h2 className="text-2xl font-bold mb-4">
            Tu restaurante funcionando en minutos
          </h2>
          <p className="text-dark-400">
            Configura tu carta, mesas y empleados. Empieza a tomar pedidos hoy mismo.
          </p>

          <div className="mt-12 grid grid-cols-3 gap-4">
            {['14 dias', 'Sin tarjeta', 'Sin compromiso'].map(text => (
              <div key={text} className="p-4 bg-dark-800 rounded-xl">
                <p className="text-sm text-dark-300">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
